import { A, G } from '@mobily/ts-belt';
import { recordMark } from '@stitch-fix/client-analytics-reporter';
import { assign, createMachine } from 'xstate';
import { BoughtItemsFragment, ClientWardrobeDataFragment } from '../../hooks/getBoughtItems';
interface BoughtItemsContext {
  isGotoItem?: boolean;
  data?: BoughtItemsFragment;
  error?: string;
}
type BoughtItemsEvent = {
  type: 'RESOLVE';
  data?: ClientWardrobeDataFragment;
} | {
  type: 'REJECT';
  error: string;
} | {
  type: 'UPDATE';
} | {
  type: 'REMOVE_BOUGHT_ITEM';
  removedSkuId: string;
  goToPage: (pageCount: number) => void;
} | {
  type: 'REMOVE_TOP_ITEM';
  removedSkuId: string;
} | {
  type: 'ADD_TOP_ITEM';
  addedSkuId: string;
};
type BoughtItemsTypestate = {
  value: 'empty';
  context: BoughtItemsContext & {
    data: BoughtItemsFragment;
    error: undefined;
  };
} | {
  value: 'loading';
  context: BoughtItemsContext & {
    data: undefined;
    error: undefined;
  };
} | {
  value: 'success';
  context: BoughtItemsContext & {
    data: BoughtItemsFragment;
    error: undefined;
  };
} | {
  value: 'failure';
  context: BoughtItemsFragment & {
    data: undefined;
    error: string;
  };
};
const buildNewBoughtItems = (updatedSkuId: string, boughtItemsData: BoughtItemsFragment, isGoToItem: boolean) => {
  return boughtItemsData.purchasesByPage.edges.map(edge => {
    if (edge.node.variant.sku === updatedSkuId) {
      const newNode = {
        ...edge,
        node: {
          ...edge.node,
          variant: {
            ...edge.node.variant,
            isGoToItem
          }
        }
      };
      return newNode;
    }
    return edge;
  });
};

/**
 * The boughtItems machine handles states regarding initial and subsequent requests
 * for bought items. A simple diagram of possible states looks like the
 * following.
 *
 * - loading
 * - success (bought items requested)
 * - empty (no bought items)
 * - failure (error from request)
 */
export const boughtItemsMachine = createMachine<BoughtItemsContext, BoughtItemsEvent, BoughtItemsTypestate>({
  id: 'boughtItems',
  initial: 'loading',
  states: {
    loading: {
      on: {
        RESOLVE: [{
          target: 'success',
          cond: 'hasResults'
        }, {
          target: 'empty'
        }],
        REJECT: {
          target: 'failure'
        }
      }
    },
    success: {
      entry: ['handleEventAssign', 'recordQueryCompleted'],
      on: {
        RESOLVE: {
          target: 'success',
          internal: true,
          actions: ['handleEventAssign']
        },
        UPDATE: {
          target: 'loading'
        },
        REMOVE_TOP_ITEM: {
          target: 'success',
          internal: true,
          actions: ['handleEventAssign']
        },
        ADD_TOP_ITEM: {
          target: 'success',
          internal: true,
          actions: ['handleEventAssign']
        },
        REMOVE_BOUGHT_ITEM: [{
          target: 'empty',
          cond: 'hasEmptyResults',
          actions: ['handleEventAssign']
        }, {
          target: 'success',
          internal: true,
          actions: ['handleEventAssign']
        }]
      }
    },
    failure: {
      entry: ['handleEventAssign']
    },
    empty: {
      entry: ['handleEventAssign']
    }
  }
}, {
  actions: {
    handleEventAssign: assign((ctx, e) => {
      switch (e.type) {
        case 'REJECT':
          return {
            error: e.error
          };
        case 'RESOLVE':
          return {
            data: e.data?.purchasesCollection
          };
        case 'REMOVE_TOP_ITEM':
          {
            if (G.isObject(ctx.data)) {
              const newBoughtItems = buildNewBoughtItems(e.removedSkuId, ctx.data, false);
              return {
                ...ctx,
                data: {
                  ...ctx.data,
                  purchasesByPage: {
                    ...ctx.data?.purchasesByPage,
                    edges: newBoughtItems
                  }
                }
              };
            }
            return ctx;
          }
        case 'REMOVE_BOUGHT_ITEM':
          {
            const removedSkuId = e.removedSkuId;
            const newBoughtItems = ctx.data?.purchasesByPage.edges.filter(item => item.node.sku !== removedSkuId && !item.node.variant.isHidden);
            if (G.isObject(ctx.data) && G.isArray(newBoughtItems)) {
              const pageCount = ctx.data.purchasesByPage.pageCount;
              if (A.isEmpty(newBoughtItems) && pageCount > 1) {
                const previousPage = pageCount - 1;
                e.goToPage(previousPage);
                return ctx;
              }
              return {
                ...ctx,
                data: {
                  ...ctx.data,
                  purchasesByPage: {
                    ...ctx.data?.purchasesByPage,
                    edges: newBoughtItems
                  }
                }
              };
            }
            return ctx;
          }
        case 'ADD_TOP_ITEM':
          {
            if (G.isObject(ctx.data)) {
              const newBoughtItems = buildNewBoughtItems(e.addedSkuId, ctx.data, true);
              return {
                ...ctx,
                data: {
                  ...ctx.data,
                  purchasesByPage: {
                    ...ctx.data?.purchasesByPage,
                    edges: newBoughtItems
                  }
                }
              };
            }
            return {
              data: ctx.data
            };
          }
        default:
          return {};
      }
    }),
    recordQueryCompleted: (_, e) => {
      if (e.type === 'RESOLVE') {
        recordMark('get_bought_items_query_completed', {
          graph_query_succeeded: true
        });
      }
    }
  },
  guards: {
    hasResults: (_, e) => {
      return Boolean(e.type === 'RESOLVE' && e.data && e.data.purchasesCollection.purchasesByPage.edges.length > 0);
    },
    hasEmptyResults: (ctx, e) => {
      return Boolean(e.type === 'REMOVE_BOUGHT_ITEM' && ctx.data && ctx.data.purchasesByPage.edges.filter(item => !item.node.variant.isHidden).length === 0 && ctx.data.purchasesByPage.pageCount <= 1);
    }
  }
});